import { useState, useMemo } from 'react'
import type { GeneratorParams } from '../../lib/generator'

const CULTURES = [
  { value: 'kaldor', label: 'Kaldorian (Feudal)' },
  { value: 'pagaelin', label: 'Pagaelin (Tribal)' },
]

const SOCIAL_CLASSES: { value: string; label: string; culture: string }[] = [
  { value: '', label: '— Random —', culture: 'kaldor' },
  { value: 'noble', label: 'Noble', culture: 'kaldor' },
  { value: 'merchant', label: 'Merchant', culture: 'kaldor' },
  { value: 'warrior', label: 'Warrior', culture: 'kaldor' },
  { value: 'soldier', label: 'Soldier', culture: 'kaldor' },
  { value: 'peasant', label: 'Peasant', culture: 'kaldor' },
  { value: 'artisan', label: 'Artisan', culture: 'kaldor' },
  { value: 'clergy', label: 'Clergy', culture: 'kaldor' },
  { value: 'unguilded', label: 'Unguilded', culture: 'kaldor' },
  { value: 'lia_kavair', label: 'Lia-Kavair', culture: 'kaldor' },
  { value: 'priest_naveh', label: 'Priest of Naveh', culture: 'kaldor' },
  { value: 'guilded_innkeeper', label: 'Guilded: Innkeeper', culture: 'kaldor' },
  { value: 'guilded_physician', label: 'Guilded: Physician', culture: 'kaldor' },
  { value: 'guilded_litigant', label: 'Guilded: Litigant', culture: 'kaldor' },
  { value: 'guilded_performer', label: 'Guilded: Performer', culture: 'kaldor' },
  { value: 'guilded_mariner', label: 'Guilded: Mariner', culture: 'kaldor' },
  { value: 'guilded_miner', label: 'Guilded: Miner', culture: 'kaldor' },
  { value: 'guilded_courtesan', label: 'Guilded: Courtesan', culture: 'kaldor' },
  { value: 'guilded_herald', label: 'Guilded: Herald', culture: 'kaldor' },
  { value: 'guilded_arcanist', label: 'Guilded: Arcanist', culture: 'kaldor' },
  { value: '', label: '— Random —', culture: 'pagaelin' },
  { value: 'pagaelin', label: 'Pagaelin', culture: 'pagaelin' },
  { value: 'walker_shaman', label: 'Walker / Shaman', culture: 'pagaelin' },
]

// Occupations per social class — curated from engine occupation pools
function titleCase(s: string) { return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }

const OCCUPATIONS_BY_CLASS: Record<string, { value: string; label: string }[]> = {
  noble: [
    { value: 'bailiff', label: 'Bailiff' },
    { value: 'herald', label: 'Herald' },
    { value: 'warrior', label: 'Warrior' },
  ],
  merchant: [
    { value: 'mercantyler', label: 'Mercantyler' },
  ],
  warrior: [
    { value: 'warrior', label: 'Knight — Medium Horse' },
    { value: 'warrior_hh', label: 'Knight — Heavy Horse' },
  ],
  soldier: [
    { value: 'soldier', label: 'Yeoman — Light Foot' },
    { value: 'soldier_mf', label: 'Guardsman — Medium Foot' },
    { value: 'soldier_sb', label: 'Yeoman — Shortbow' },
    { value: 'soldier_lb', label: 'Yeoman — Longbow' },
  ],
  clergy: [
    { value: 'clergy', label: 'Clergy (deity-based)' },
  ],
  lia_kavair: [
    { value: 'lia_kavair', label: 'Lia-Kavair' },
  ],
  // Peasant and unguilded both draw from the same UNGUILDED_POOL in the engine
  peasant: [
    ...[
      'animal_trainer','beggar','cartographer','cook','farmer','fisherman',
      'gladiator','herdsman','hunter','laborer','longshoreman','prostitute',
      'ratter','sage','scribe','soldier','teamster','thatcher','toymaker',
    ].map(o => ({ value: o, label: titleCase(o) })),
  ],
  unguilded: [
    ...[
      'animal_trainer','beggar','cartographer','cook','farmer','fisherman',
      'gladiator','herdsman','hunter','laborer','longshoreman','prostitute',
      'ratter','sage','scribe','soldier','teamster','thatcher','toymaker',
    ].map(o => ({ value: o, label: titleCase(o) })),
  ],
  artisan: [
    ...[
      'alchemist','apothecary','astrologer','chandler','charcoaler','clothier',
      'embalmer','glassworker','harper','hideworker','jeweler','lexigrapher',
      'locksmith','mason','metalsmith','miller','ostler','perfumer','pilot',
      'potter','salter','shipwright','tentmaker','timberwright',
      'weaponcrafter','woodcrafter',
    ].map(o => ({ value: o, label: titleCase(o) })),
  ],
  guilded_innkeeper: [{ value: 'innkeeper', label: 'Innkeeper' }],
  guilded_physician: [{ value: 'physician', label: 'Physician' }],
  guilded_litigant: [{ value: 'litigant', label: 'Litigant' }],
  guilded_performer: [
    { value: 'harper', label: 'Harper' },
    { value: 'thespian', label: 'Thespian' },
  ],
  guilded_mariner: [{ value: 'seaman', label: 'Seaman' }],
  guilded_miner: [{ value: 'miner', label: 'Miner' }],
  guilded_courtesan: [{ value: 'courtesan', label: 'Courtesan' }],
  guilded_herald: [{ value: 'herald', label: 'Herald' }],
  guilded_arcanist: [{ value: 'alchemist', label: 'Alchemist' }],
  // Pagaelin classes have fixed occupations
  pagaelin: [],
  walker_shaman: [],
}

function getOccupationsForClass(cls: string): { value: string; label: string }[] {
  return OCCUPATIONS_BY_CLASS[cls] ?? []
}

// Conditions organised by category with culture and class relevance
interface ConditionDef { id: string; label: string; culture?: string; classes?: string[] }

const GUILDED_CLASSES = ['artisan','merchant','guilded_innkeeper','guilded_physician','guilded_litigant','guilded_performer','guilded_mariner','guilded_miner','guilded_courtesan','guilded_herald','guilded_arcanist']

const ALL_CONDITIONS: ConditionDef[] = [
  // ── Social / Professional ──
  { id: 'guild_member', label: 'Guild member', culture: 'kaldor', classes: GUILDED_CLASSES },
  { id: 'guild_master', label: 'Guild master', culture: 'kaldor', classes: GUILDED_CLASSES },
  { id: 'guild_officer', label: 'Guild officer / syndic', culture: 'kaldor', classes: GUILDED_CLASSES },
  { id: 'franchise_holder', label: 'Franchise holder', culture: 'kaldor', classes: GUILDED_CLASSES },
  { id: 'bonded_master', label: 'Bonded master', culture: 'kaldor', classes: GUILDED_CLASSES },
  { id: 'skilled_artisan', label: 'Skilled artisan', culture: 'kaldor', classes: GUILDED_CLASSES },
  { id: 'masterwork_achieved', label: 'Masterwork achieved', culture: 'kaldor', classes: GUILDED_CLASSES },
  { id: 'knighted', label: 'Knighted', culture: 'kaldor', classes: ['noble','warrior','soldier'] },
  { id: 'landed', label: 'Landed (estate)', culture: 'kaldor', classes: ['noble'] },
  { id: 'landless', label: 'Landless noble', culture: 'kaldor', classes: ['noble'] },
  { id: 'enfeoffed', label: 'Enfeoffed (granted fief)', culture: 'kaldor', classes: ['noble'] },
  { id: 'succession_resolved', label: 'Succession resolved', culture: 'kaldor', classes: ['noble'] },
  { id: 'feud', label: 'Active feud', culture: 'kaldor', classes: ['noble','merchant','artisan'] },
  { id: 'veteran', label: 'Veteran', culture: 'kaldor', classes: ['warrior','soldier','noble'] },
  { id: 'sergeant', label: 'Sergeant', culture: 'kaldor', classes: ['warrior','soldier'] },
  { id: 'military_posting', label: 'Military posting', culture: 'kaldor', classes: ['warrior','soldier','noble'] },
  { id: 'military_campaign', label: 'Military campaign', culture: 'kaldor', classes: ['warrior','soldier','noble'] },
  { id: 'deserter', label: 'Deserter', culture: 'kaldor', classes: ['warrior','soldier'] },
  { id: 'pendragon_champion', label: 'Pendragon champion', culture: 'kaldor', classes: ['noble','warrior'] },
  { id: 'tournament_veteran', label: 'Tournament veteran', culture: 'kaldor', classes: ['noble','warrior'] },

  // ── Clergy ranks (Laranian progression) ──
  { id: 'clergy_postulant', label: 'Postulant (Ashesa)', culture: 'kaldor', classes: ['clergy'] },
  { id: 'clergy_acolyte', label: 'Acolyte', culture: 'kaldor', classes: ['clergy'] },
  { id: 'clergy_priest', label: 'Ordained priest (Matakea)', culture: 'kaldor', classes: ['clergy'] },
  { id: 'clergy_vicar', label: 'Vicar (chaplain posting)', culture: 'kaldor', classes: ['clergy'] },
  { id: 'clergy_canon', label: 'Canon (High Priest)', culture: 'kaldor', classes: ['clergy'] },
  { id: 'clergy_prior', label: 'Prior (abbey)', culture: 'kaldor', classes: ['clergy'] },
  { id: 'clergy_bishop', label: 'Bishop (Rekela)', culture: 'kaldor', classes: ['clergy'] },
  { id: 'peoni_celibate', label: 'Peoni celibacy vow', culture: 'kaldor', classes: ['clergy'] },

  // ── Naveh temple ──
  { id: 'naveh_initiated', label: 'Naveh initiated (Temple Maze)', culture: 'kaldor', classes: ['priest_naveh'] },
  { id: 'naveh_has_cover', label: 'Has civilian cover', culture: 'kaldor', classes: ['priest_naveh'] },
  { id: 'naveh_cover_embedded', label: 'Deep cover integration', culture: 'kaldor', classes: ['priest_naveh'] },
  { id: 'naveh_navas_kara', label: 'Has killed for the order', culture: 'kaldor', classes: ['priest_naveh'] },
  { id: 'naveh_is_master', label: 'Master (one of five)', culture: 'kaldor', classes: ['priest_naveh'] },
  { id: 'naveh_sleeper', label: 'Sleeper (deep cover, no duties)', culture: 'kaldor', classes: ['priest_naveh'] },

  // ── Lia-Kavair ranks ──
  { id: 'lk_initiated', label: 'LK initiated', culture: 'kaldor', classes: ['lia_kavair'] },
  { id: 'lk_has_worked', label: 'LK has completed jobs', culture: 'kaldor', classes: ['lia_kavair'] },
  { id: 'lk_master_rank', label: 'LK master rank', culture: 'kaldor', classes: ['lia_kavair'] },
  { id: 'lk_has_cover', label: 'LK has cover identity', culture: 'kaldor', classes: ['lia_kavair'] },
  { id: 'lk_expelled', label: 'LK expelled', culture: 'kaldor', classes: ['lia_kavair'] },

  // ── Economic / Social ──
  { id: 'prosperous', label: 'Prosperous', culture: 'kaldor' },
  { id: 'indebted', label: 'Indebted', culture: 'kaldor' },
  { id: 'ruined', label: 'Ruined (financial)', culture: 'kaldor' },
  { id: 'destitute', label: 'Destitute', culture: 'kaldor' },
  { id: 'retired', label: 'Retired', culture: 'kaldor' },

  // ── Faith / Religion ──
  { id: 'devout', label: 'Devout', culture: 'kaldor' },
  { id: 'faith_crisis', label: 'Faith crisis', culture: 'kaldor' },
  { id: 'secret_worshipper', label: 'Secret worshipper (banned deity)', culture: 'kaldor' },
  { id: 'pilgrim', label: 'Pilgrim', culture: 'kaldor' },

  // ── Background / History ──
  { id: 'orphaned', label: 'Orphaned', culture: 'kaldor' },
  { id: 'exiled', label: 'Exiled', culture: 'kaldor', classes: ['noble'] },
  { id: 'outlawed', label: 'Outlawed', culture: 'kaldor' },
  { id: 'captured', label: 'Captured / prisoner', culture: 'kaldor' },
  { id: 'branded', label: 'Branded (convicted)', culture: 'kaldor' },
  { id: 'criminal_record', label: 'Criminal record', culture: 'kaldor' },
  { id: 'freeman_established', label: 'Freeman (year-and-a-day)', culture: 'kaldor', classes: ['unguilded','peasant'] },

  // ── Languages / Border ──
  { id: 'lang_pagaelin_pidgin', label: 'Speaks Pagaelin (pidgin)', culture: 'kaldor' },
  { id: 'lang_pagaelin_fluent', label: 'Speaks Pagaelin (fluent)', culture: 'kaldor' },
  { id: 'border_posting_oselmarch', label: 'Oselmarch border posting', culture: 'kaldor', classes: ['warrior','soldier','noble'] },
  { id: 'oselbridge_contact', label: 'Oselbridge trading contact', culture: 'kaldor' },

  // ── Physical / Health ──
  { id: 'lame', label: 'Lame' },
  { id: 'scarred', label: 'Scarred' },
  { id: 'blind_one_eye', label: 'Blind (one eye)' },
  { id: 'chronic_illness', label: 'Chronic illness' },
  { id: 'robust', label: 'Robust (unusually healthy)' },

  // ── Pagaelin ──
  { id: 'first_kill', label: 'First kill', culture: 'pagaelin' },
  { id: 'holder_relationship', label: 'Has a woman / Is held', culture: 'pagaelin' },
  { id: 'established_household', label: 'Established household', culture: 'pagaelin' },
  { id: 'pagaelin_chief', label: 'Chieftain', culture: 'pagaelin' },
  { id: 'pagaelin_deposed', label: 'Former chieftain (deposed)', culture: 'pagaelin' },
  { id: 'pagaelin_held', label: 'Held woman', culture: 'pagaelin' },
  { id: 'tribal_alignment_traditional', label: 'Traditional-aligned', culture: 'pagaelin' },
  { id: 'tribal_alignment_syncretist', label: 'Syncretist (hybrid faith)', culture: 'pagaelin' },
  { id: 'tribal_alignment_walker', label: 'Walker-aligned', culture: 'pagaelin' },
  { id: 'tribal_alignment_dominated', label: 'Walker-dominated', culture: 'pagaelin' },
  { id: 'shaman_calling', label: 'Shaman calling', culture: 'pagaelin', classes: ['walker_shaman'] },
  { id: 'raunir_ordeal_survived', label: 'Survived Akan-shri', culture: 'pagaelin', classes: ['walker_shaman'] },
  { id: 'lodge_leverage', label: 'Lodge has leverage', culture: 'pagaelin', classes: ['walker_shaman'] },
  { id: 'lang_harnic_trade', label: 'Speaks Harnic (trade)', culture: 'pagaelin' },
  { id: 'lang_harnic_fluent', label: 'Speaks Harnic (fluent)', culture: 'pagaelin' },
]

interface EventDef {
  id: string; label: string; culture?: string; classes?: string[]
  requireConditions?: string[]; excludeConditions?: string[]; addsConditions?: string[]
}

const EVENTS: EventDef[] = [
  // ── Kaldorian ──
  { id: 'military_campaign', label: 'Military campaign', culture: 'kaldor', classes: ['warrior','soldier','noble'], excludeConditions: ['lame','exiled'], addsConditions: ['veteran'] },
  { id: 'pilgrimage', label: 'Pilgrimage', culture: 'kaldor' },
  { id: 'knighted', label: 'Knighted', culture: 'kaldor', classes: ['noble','warrior'], excludeConditions: ['knighted','exiled'], addsConditions: ['knighted'] },
  { id: 'guild_advancement', label: 'Guild advancement', culture: 'kaldor', classes: GUILDED_CLASSES, requireConditions: ['guild_member'], addsConditions: ['guild_master'] },
  { id: 'joined_guild', label: 'Joined guild', culture: 'kaldor', classes: GUILDED_CLASSES, excludeConditions: ['guild_member','guild_master','outlawed'], addsConditions: ['guild_member'] },
  { id: 'noble_exile', label: 'Noble exile', culture: 'kaldor', classes: ['noble'], excludeConditions: ['exiled'], addsConditions: ['exiled'] },
  { id: 'survived_famine', label: 'Survived famine', culture: 'kaldor' },
  { id: 'war_wound', label: 'War wound', culture: 'kaldor', classes: ['warrior','soldier','noble'], requireConditions: ['veteran'], addsConditions: ['scarred'] },
  { id: 'captured_prisoner', label: 'Captured/prisoner', culture: 'kaldor', excludeConditions: ['captured'], addsConditions: ['captured'] },
  // ── Pagaelin ──
  { id: 'pagaelin_first_kill', label: 'First kill', culture: 'pagaelin', excludeConditions: ['first_kill'], addsConditions: ['first_kill'] },
  { id: 'pagaelin_took_a_wife', label: 'Took a woman', culture: 'pagaelin', requireConditions: ['first_kill'], addsConditions: ['holder_relationship'] },
  { id: 'pagaelin_seize_chieftaincy', label: 'Seized chieftaincy', culture: 'pagaelin', requireConditions: ['first_kill','established_household'], excludeConditions: ['pagaelin_chief'], addsConditions: ['pagaelin_chief'] },
  { id: 'pagaelin_akan_shri_ordeal', label: 'Akan-shri ordeal', culture: 'pagaelin', classes: ['walker_shaman'], requireConditions: ['tribal_alignment_walker'], excludeConditions: ['raunir_ordeal_survived'], addsConditions: ['raunir_ordeal_survived'] },
  { id: 'pagaelin_shaman_calling', label: 'Shaman calling', culture: 'pagaelin', classes: ['walker_shaman'], excludeConditions: ['shaman_calling'], addsConditions: ['shaman_calling'] },
  { id: 'pagaelin_raid_wounded', label: 'Wounded in raid', culture: 'pagaelin', addsConditions: ['scarred'] },
  { id: 'pagaelin_tribe_split', label: 'Tribe split', culture: 'pagaelin' },
  { id: 'pagaelin_lodge_assignment', label: 'Lodge assignment', culture: 'pagaelin', classes: ['walker_shaman'] },
  { id: 'pagaelin_former_chief_deposed', label: 'Lost chieftaincy', culture: 'pagaelin', requireConditions: ['pagaelin_chief'], addsConditions: ['pagaelin_deposed'] },
]

interface Props {
  onGenerate: (params: GeneratorParams) => void
  isGenerating: boolean
  onCultureChange?: () => void
}

export function ShadowParametersForm({ onGenerate, isGenerating, onCultureChange }: Props) {
  const [culture, setCulture] = useState<string>('')
  const [name, setName] = useState('')
  const [seed, setSeed] = useState<number | ''>('')
  const [socialClass, setSocialClass] = useState('')
  const [sex, setSex] = useState<'male' | 'female' | ''>('male')
  const [targetAge, setTargetAge] = useState<number | ''>(30)
  const [importance, setImportance] = useState<'regular' | 'important'>('regular')
  const [occupation, setOccupation] = useState('')
  const [location, setLocation] = useState('')
  const [maritalStatus, setMaritalStatus] = useState<'married' | 'widowed' | 'single' | ''>('')
  const [childrenAlive, setChildrenAlive] = useState<number | ''>('')
  const [requiredConditions, setRequiredConditions] = useState<string[]>([])
  const [forbiddenConditions, setForbiddenConditions] = useState<string[]>([])
  const [requiredEvents, setRequiredEvents] = useState<string[]>([])
  const [deity, setDeity] = useState('')
  const [description, setDescription] = useState('')
  const [aiAppearance, setAiAppearance] = useState(false)
  // Pagaelin family
  const [parentClass, setParentClass] = useState('')
  const [pagaelinIsHeld, setPagaelinIsHeld] = useState(false)
  const [pagaelinHeldWomen, setPagaelinHeldWomen] = useState<number | ''>('')

  // Filter social classes by selected culture
  const availableClasses = useMemo(() =>
    SOCIAL_CLASSES.filter(c => c.culture === culture),
    [culture]
  )

  // Filter conditions and events by culture and social class
  const filteredConditions = useMemo(() => {
    return ALL_CONDITIONS
      .filter(c => !c.culture || c.culture === culture)
      .filter(c => !socialClass || !c.classes || c.classes.includes(socialClass))
  }, [culture, socialClass])

  const filteredEvents = useMemo(() => {
    return EVENTS
      .filter(e => !e.culture || e.culture === culture)
      .filter(e => !socialClass || !e.classes || e.classes.includes(socialClass))
  }, [culture, socialClass])

  // Reset everything when culture changes
  const handleCultureChange = (newCulture: string) => {
    setCulture(newCulture)
    setSocialClass('')
    setMaritalStatus('')
    setPagaelinIsHeld(false)
    setPagaelinHeldWomen('')
    setRequiredConditions([])
    setForbiddenConditions([])
    setRequiredEvents([])
    onCultureChange?.()
  }

  // Clear conditions/events that are no longer valid when class changes
  const handleClassChange = (newClass: string) => {
    setSocialClass(newClass)
    // Auto-populate occupation if class has only one option, otherwise reset
    const occs = getOccupationsForClass(newClass)
    setOccupation(occs.length === 1 ? occs[0].value : '')
    if (newClass) {
      setRequiredConditions(prev => prev.filter(c => {
        const cond = ALL_CONDITIONS.find(ac => ac.id === c)
        return !cond?.classes || cond.classes.includes(newClass)
      }))
      setForbiddenConditions(prev => prev.filter(c => {
        const cond = ALL_CONDITIONS.find(ac => ac.id === c)
        return !cond?.classes || cond.classes.includes(newClass)
      }))
      setRequiredEvents(prev => prev.filter(e => {
        const ev = EVENTS.find(ae => ae.id === e)
        return !ev?.classes || ev.classes.includes(newClass)
      }))
    }
  }

  const toggleForbiddenCondition = (condId: string) => {
    if (forbiddenConditions.includes(condId)) {
      setForbiddenConditions(prev => prev.filter(c => c !== condId))
    } else {
      setForbiddenConditions(prev => [...prev, condId])
      // Deselect any required events that need or produce this condition
      setRequiredEvents(prev => prev.filter(evId => {
        const ev = EVENTS.find(e => e.id === evId)
        const requires = (ev?.requireConditions ?? []).includes(condId)
        const adds = (ev?.addsConditions ?? []).includes(condId)
        return !requires && !adds
      }))
      // Also remove the condition from required if it was there
      setRequiredConditions(prev => prev.filter(c => c !== condId))
    }
  }

  // Conditions forced by currently selected required events
  const eventRequiredConditions = useMemo(() => {
    const set = new Set<string>()
    for (const evId of requiredEvents) {
      const ev = EVENTS.find(e => e.id === evId)
      for (const c of ev?.requireConditions ?? []) set.add(c)
    }
    return set
  }, [requiredEvents])

  // Conditions forbidden by currently selected required events
  const eventExcludedConditions = useMemo(() => {
    const set = new Set<string>()
    for (const evId of requiredEvents) {
      const ev = EVENTS.find(e => e.id === evId)
      for (const c of ev?.excludeConditions ?? []) set.add(c)
    }
    return set
  }, [requiredEvents])

  // Mutually exclusive condition pairs — requiring one forbids the other
  const CONDITION_CONFLICTS: Record<string, string[]> = {
    prosperous: ['indebted', 'destitute', 'ruined'],
    indebted: ['prosperous'],
    destitute: ['prosperous'],
    ruined: ['prosperous'],
    devout: ['faith_crisis'],
    faith_crisis: ['devout'],
    married: ['celibate'],
    celibate: ['married'],
    knighted: ['outlawed', 'deserter'],
    outlawed: ['knighted'],
    deserter: ['knighted'],
  }

  const toggleRequiredCondition = (condId: string) => {
    if (requiredConditions.includes(condId)) {
      // Deselecting — also deselect any events that require this condition
      setRequiredConditions(prev => prev.filter(c => c !== condId))
      // Remove auto-forbidden conflicts
      const conflicts = CONDITION_CONFLICTS[condId] ?? []
      setForbiddenConditions(prev => prev.filter(c => !conflicts.includes(c)))
      setRequiredEvents(prev => prev.filter(evId => {
        const ev = EVENTS.find(e => e.id === evId)
        return !(ev?.requireConditions ?? []).includes(condId)
      }))
    } else {
      setRequiredConditions(prev => [...prev, condId])
      // Auto-forbid conflicting conditions and remove them from required
      const conflicts = CONDITION_CONFLICTS[condId] ?? []
      if (conflicts.length > 0) {
        setForbiddenConditions(prev => [...new Set([...prev, ...conflicts])])
        setRequiredConditions(prev => prev.filter(c => !conflicts.includes(c)))
      }
    }
  }

  const toggleRequiredEvent = (evId: string) => {
    if (requiredEvents.includes(evId)) {
      // Deselecting event — remove auto-forced conditions (unless another event still needs them)
      const newEvents = requiredEvents.filter(e => e !== evId)
      setRequiredEvents(newEvents)
      // Recompute which conditions are still forced by remaining events
      const stillForced = new Set<string>()
      for (const eId of newEvents) {
        const ev = EVENTS.find(e => e.id === eId)
        for (const c of ev?.requireConditions ?? []) stillForced.add(c)
      }
      // Remove conditions that were only forced by the deselected event
      const removed = EVENTS.find(e => e.id === evId)
      for (const c of removed?.requireConditions ?? []) {
        if (!stillForced.has(c)) {
          setRequiredConditions(prev => prev.filter(rc => rc !== c))
        }
      }
      // Also remove excluded conditions from forbidden that were only from this event
      const stillExcluded = new Set<string>()
      for (const eId of newEvents) {
        const ev = EVENTS.find(e => e.id === eId)
        for (const c of ev?.excludeConditions ?? []) stillExcluded.add(c)
      }
      const removedExcl = removed?.excludeConditions ?? []
      for (const c of removedExcl) {
        if (!stillExcluded.has(c)) {
          setForbiddenConditions(prev => prev.filter(fc => fc !== c))
        }
      }
    } else {
      // Selecting event — auto-add required conditions, auto-forbid excluded conditions
      setRequiredEvents(prev => [...prev, evId])
      const ev = EVENTS.find(e => e.id === evId)
      for (const c of ev?.requireConditions ?? []) {
        setRequiredConditions(prev => prev.includes(c) ? prev : [...prev, c])
        setForbiddenConditions(prev => prev.filter(fc => fc !== c))
      }
      for (const c of ev?.excludeConditions ?? []) {
        setForbiddenConditions(prev => prev.includes(c) ? prev : [...prev, c])
        setRequiredConditions(prev => prev.filter(rc => rc !== c))
      }
    }
  }

  const handleGenerate = () => {
    const params: GeneratorParams = {
      gameYear: 720,
      targetAge: (targetAge as number) || 30,
      culture: culture || undefined,
    }
    if (seed) params.seed = seed as number
    if (name.trim()) (params as Record<string, unknown>).name = name.trim()
    // When culture is pagaelin but no class selected, default to 'pagaelin' class
    // (the engine ignores the culture param and defaults to kaldorian classes)
    if (socialClass) {
      params.socialClass = socialClass
    } else if (culture === 'pagaelin') {
      params.socialClass = 'pagaelin'
    }
    if (sex) params.sex = sex
    if (occupation) params.occupation = occupation
    if (location) params.location = location
    if (parentClass) (params as Record<string, unknown>).parentClass = parentClass
    if (deity) (params as Record<string, unknown>).publicDeity = deity
    if (maritalStatus) params.maritalStatus = maritalStatus
    if (childrenAlive !== '') params.childrenAlive = childrenAlive as number
    // Pagaelin family → inject conditions/events
    const extraConditions = [...requiredConditions]
    const extraEvents = [...requiredEvents]
    if (culture === 'pagaelin') {
      if (sex === 'female' && pagaelinIsHeld) {
        if (!extraConditions.includes('pagaelin_held')) extraConditions.push('pagaelin_held')
        if (!extraConditions.includes('holder_relationship')) extraConditions.push('holder_relationship')
      }
      if (sex === 'male' && pagaelinHeldWomen !== '' && pagaelinHeldWomen > 0) {
        if (!extraConditions.includes('first_kill')) extraConditions.push('first_kill')
        if (!extraConditions.includes('holder_relationship')) extraConditions.push('holder_relationship')
      }
    }
    if (extraConditions.length) params.requiredConditions = extraConditions
    if (forbiddenConditions.length) params.forbiddenConditions = forbiddenConditions
    if (extraEvents.length) params.requiredEvents = extraEvents
    if (description) params.unmappedNotes = [description]
    ;(params as Record<string, unknown>).importance = importance
    params.aiAppearance = aiAppearance

    onGenerate(params)
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-accent">Shadow Parameters</h3>
      <p className="text-sm text-ink-light">Set specific values or leave blank for random generation.</p>

      {/* Culture — must be selected first */}
      <div>
        <label className="block text-xs font-medium mb-1">Culture</label>
        <div className="flex gap-2">
          {CULTURES.map(c => (
            <button key={c.value} onClick={() => handleCultureChange(c.value)}
              className={`flex-1 py-2 text-sm rounded font-medium ${
                culture === c.value
                  ? 'bg-accent text-white'
                  : 'border border-parchment-dark hover:bg-parchment-dark'
              }`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {!culture && (
        <p className="text-ink-light text-sm italic text-center py-4">Select a culture to continue.</p>
      )}

      {culture && <>

      {/* Name, Importance & Seed */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Name</label>
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder="Leave blank for random"
            className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Importance</label>
          <select value={importance} onChange={e => setImportance(e.target.value as 'regular' | 'important')}
            className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm">
            <option value="regular">Regular (3d6 stats)</option>
            <option value="important">Important (4d6 drop lowest)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Seed</label>
          <input type="number" value={seed} onChange={e => setSeed(e.target.value ? parseInt(e.target.value) : '')}
            placeholder="Random"
            className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm font-mono" />
        </div>
      </div>

      {/* Identity */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Social Class</label>
          <select value={socialClass} onChange={e => handleClassChange(e.target.value)}
            className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm">
            {availableClasses.map(c => <option key={c.value + c.culture} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Sex</label>
          <select value={sex} onChange={e => setSex(e.target.value as typeof sex)}
            className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm">
            <option value="">— Random —</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Target Age</label>
          <input type="number" min={18} max={80} value={targetAge}
            onChange={e => setTargetAge(e.target.value ? parseInt(e.target.value) : '')}
            className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Occupation</label>
          <select value={occupation} onChange={e => setOccupation(e.target.value)}
            className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm">
            <option value="">— Random —</option>
            {getOccupationsForClass(socialClass).map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Location</label>
          <select value={location} onChange={e => setLocation(e.target.value)}
            className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm">
            <option value="">— Random —</option>
            <option value="hamlet">Hamlet</option>
            <option value="village">Village</option>
            <option value="town">Town</option>
            <option value="city">City</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Parent's Social Class</label>
          <select value={parentClass} onChange={e => setParentClass(e.target.value)}
            className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm">
            <option value="">{socialClass ? `Same as character (${availableClasses.find(c => c.value === socialClass)?.label ?? socialClass})` : '— Same as character —'}</option>
            {availableClasses.filter(c => c.value !== '').map(c => <option key={'parent-' + c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        {culture === 'kaldor' && (
          <div>
            <label className="block text-xs font-medium mb-1">Deity</label>
            <select value={deity} onChange={e => setDeity(e.target.value)}
              className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm">
              <option value="">— Random —</option>
              <option value="Larani">Larani (justice, chivalry)</option>
              <option value="Peoni">Peoni (healing, agriculture)</option>
              <option value="Halea">Halea (wealth, pleasure)</option>
              <option value="Save-Knor">Save K'nor (knowledge)</option>
              <option value="Ilvir">Ilvir (creatures, dreams)</option>
              <option value="Sarajin">Sarajin (battle, honour)</option>
              <option value="Siem">Siem (elves, nature)</option>
              <option value="Agrik">Agrik (fire, war)</option>
              <option value="Morgath">Morgath (undead, chaos)</option>
              <option value="Naveh">Naveh (assassination, darkness)</option>
            </select>
          </div>
        )}
        {culture === 'kaldor' && (
          <div>
            <label className="block text-xs font-medium mb-1">Marital Status</label>
            <select value={maritalStatus} onChange={e => setMaritalStatus(e.target.value as typeof maritalStatus)}
              className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm">
              <option value="">— Random —</option>
              <option value="married">Married</option>
              <option value="widowed">Widowed</option>
              <option value="single">Single</option>
            </select>
          </div>
        )}
        {culture === 'pagaelin' && sex === 'female' && (
          <div>
            <label className="block text-xs font-medium mb-1">Held Status</label>
            <select value={pagaelinIsHeld ? 'held' : ''}
              onChange={e => setPagaelinIsHeld(e.target.value === 'held')}
              className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm">
              <option value="">— Random —</option>
              <option value="held">Is held woman</option>
            </select>
          </div>
        )}
        {culture === 'pagaelin' && sex === 'male' && (
          <div>
            <label className="block text-xs font-medium mb-1">Held Women</label>
            <select value={pagaelinHeldWomen}
              onChange={e => setPagaelinHeldWomen(e.target.value ? parseInt(e.target.value) : '')}
              className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm">
              <option value="">— Random —</option>
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium mb-1">Children Alive</label>
          <input type="number" min={0} max={12} value={childrenAlive}
            onChange={e => setChildrenAlive(e.target.value ? parseInt(e.target.value) : '')}
            placeholder="Random"
            className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm" />
        </div>
      </div>

      {/* Required Conditions */}
      <div>
        <label className="block text-xs font-medium mb-1">
          Required Conditions
          {socialClass && <span className="text-ink-light ml-1">(filtered for {socialClass})</span>}
        </label>
        <div className="flex flex-wrap gap-1">
          {filteredConditions.map(c => {
            const isForbidden = forbiddenConditions.includes(c.id)
            const isEventForced = eventRequiredConditions.has(c.id)
            const isEventExcluded = eventExcludedConditions.has(c.id)
            const isSelected = requiredConditions.includes(c.id)
            const isDisabled = isForbidden || isEventExcluded
            return (
              <button key={c.id}
                onClick={() => !isDisabled && !isEventForced && toggleRequiredCondition(c.id)}
                disabled={isDisabled}
                className={`px-2 py-0.5 text-xs rounded ${
                  isSelected && isEventForced ? 'bg-success text-white ring-1 ring-success ring-offset-1 cursor-default'
                  : isSelected ? 'bg-success text-white'
                  : isDisabled ? 'border border-parchment-dark opacity-30 cursor-not-allowed'
                  : 'border border-parchment-dark hover:bg-parchment-dark'
                }`}
                title={isEventForced ? 'Required by selected life event' : isEventExcluded ? 'Excluded by selected life event' : undefined}
              >{c.label}{isEventForced ? ' \u2022' : ''}</button>
            )
          })}
        </div>
      </div>

      {/* Forbidden Conditions */}
      <div>
        <label className="block text-xs font-medium mb-1">Forbidden Conditions</label>
        <div className="flex flex-wrap gap-1">
          {filteredConditions.map(c => {
            const isRequired = requiredConditions.includes(c.id)
            const isEventForced = eventRequiredConditions.has(c.id)
            const isEventExcluded = eventExcludedConditions.has(c.id)
            const isSelected = forbiddenConditions.includes(c.id)
            const isDisabled = isRequired || isEventForced
            return (
              <button key={c.id}
                onClick={() => !isDisabled && !isEventExcluded && toggleForbiddenCondition(c.id)}
                disabled={isDisabled}
                className={`px-2 py-0.5 text-xs rounded ${
                  isSelected && isEventExcluded ? 'bg-danger text-white ring-1 ring-danger ring-offset-1 cursor-default'
                  : isSelected ? 'bg-danger text-white'
                  : isDisabled ? 'border border-parchment-dark opacity-30 cursor-not-allowed'
                  : 'border border-parchment-dark hover:bg-parchment-dark'
                }`}
                title={isEventExcluded ? 'Excluded by selected life event' : isEventForced ? 'Required by selected life event' : undefined}
              >{c.label}{isEventExcluded ? ' \u2022' : ''}</button>
            )
          })}
        </div>
      </div>

      {/* Required Events */}
      <div>
        <label className="block text-xs font-medium mb-1">
          Required Life Events
          {socialClass && <span className="text-ink-light ml-1">(filtered for {socialClass})</span>}
        </label>
        <div className="flex flex-wrap gap-1">
          {filteredEvents.map(e => {
            // Disable if any required condition is forbidden, or any produced condition is forbidden
            const reqBlocked = (e.requireConditions ?? []).some(c => forbiddenConditions.includes(c))
            const addsBlocked = (e.addsConditions ?? []).some(c => forbiddenConditions.includes(c))
            const isDisabled = reqBlocked || addsBlocked
            const isSelected = requiredEvents.includes(e.id)
            return (
              <button key={e.id}
                onClick={() => !isDisabled && toggleRequiredEvent(e.id)}
                disabled={isDisabled}
                className={`px-2 py-0.5 text-xs rounded ${
                  isSelected ? 'bg-info text-white'
                  : isDisabled ? 'border border-parchment-dark opacity-30 cursor-not-allowed'
                  : 'border border-parchment-dark hover:bg-parchment-dark'
                }`}
                title={isDisabled ? `Blocked by forbidden condition: ${[...(e.requireConditions ?? []), ...(e.addsConditions ?? [])].filter(c => forbiddenConditions.includes(c)).join(', ')}` : undefined}
              >{e.label}</button>
            )
          })}
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-medium mb-1">Free Text Description</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)}
          rows={3} placeholder="e.g. A lame former soldier, married, two children..."
          className="w-full px-2 py-1.5 border border-parchment-dark rounded text-sm" />
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={aiAppearance} onChange={e => setAiAppearance(e.target.checked)}
          className="rounded border-parchment-dark" />
        <span>AI appearance descriptions</span>
        <span className="text-[10px] text-ink-light">(uses Claude API)</span>
      </label>

      <button onClick={handleGenerate} disabled={isGenerating}
        className="w-full py-2 bg-accent text-white rounded hover:bg-accent-light disabled:opacity-50 font-medium">
        {isGenerating ? 'Generating...' : 'Generate NPC'}
      </button>

      </>}
    </div>
  )
}
